import { Form, Formik, useFormikContext } from 'formik';
import { type FormikProps } from 'formik';
import { useEffect } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { SidePanel } from '@hivebrite/design-system/SidePanel';

import {
  type AIFormWidgetFormValues,
  type ParentFormikFormValues,
  type FieldSuggestion,
  type SmartSuggestion,
} from 'hb-react/shared/components/AIFormWidget/types';

import { Footer, AIFormSuggestion, Suggestion } from './components';
import { SuggestionLoader } from './components/Suggestion/components';
import { AITitle } from './ui';

interface AIFormPanelProps {
  acceptAllSuggestions: () => void;
  acceptSuggestion: (suggestion: FieldSuggestion) => void;
  enabledFields: Array<{ field: string; label: string; type: string }>;
  formik: FormikProps<ParentFormikFormValues>;
  initialValues: AIFormWidgetFormValues;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (values: AIFormWidgetFormValues) => Promise<void>;
  placeholder?: string;
  rejectSuggestion: (id: string) => void;
  resetSuggestions: () => void;
  smartSuggestions?: Array<SmartSuggestion>;
  suggestions: Array<FieldSuggestion>;
  updateSuggestion: (
    suggestion: Partial<FieldSuggestion> & { id: string },
  ) => void;
}

export const AIFormPanel = ({
  onClose,
  onSubmit,
  suggestions,
  acceptSuggestion,
  rejectSuggestion,
  updateSuggestion,
  acceptAllSuggestions,
  resetSuggestions,
  enabledFields,
  formik,
  initialValues,
  placeholder,
  smartSuggestions,
}: AIFormPanelProps) => {
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const promptInput = document.getElementById('ai-prompt-input');

      if (promptInput) {
        promptInput.focus();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <SidePanel
      isOpen={true}
      onClose={onClose}
      title={<AITitle />}
      footer={
        <Footer
          suggestions={suggestions}
          acceptAllSuggestions={acceptAllSuggestions}
          resetSuggestions={resetSuggestions}
        />
      }
    >
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        <Form>
          <Box display="flex" flexDirection="column" gridRowGap={4}>
            <FormBody
              enabledFields={enabledFields}
              suggestions={suggestions}
              acceptSuggestion={acceptSuggestion}
              rejectSuggestion={rejectSuggestion}
              updateSuggestion={updateSuggestion}
              formik={formik}
              placeholder={placeholder}
              smartSuggestions={smartSuggestions}
            />
          </Box>
        </Form>
      </Formik>
    </SidePanel>
  );
};

const FormBody = ({
  enabledFields,
  suggestions,
  acceptSuggestion,
  rejectSuggestion,
  updateSuggestion,
  formik,
  placeholder,
  smartSuggestions,
}: Pick<
  AIFormPanelProps,
  | 'acceptSuggestion'
  | 'enabledFields'
  | 'formik'
  | 'placeholder'
  | 'rejectSuggestion'
  | 'smartSuggestions'
  | 'suggestions'
  | 'updateSuggestion'
>) => {
  const { submitForm, isSubmitting, values } =
    useFormikContext<AIFormWidgetFormValues>();

  if (isSubmitting) {
    return enabledFields.map((field) => <SuggestionLoader key={field.field} />);
  }

  if (suggestions.length === 0) {
    return (
      <AIFormSuggestion
        isSubmitting={isSubmitting}
        values={values}
        submitForm={submitForm}
        placeholder={placeholder}
        smartSuggestions={smartSuggestions}
      />
    );
  }

  return suggestions.map((suggestion) => (
    <Suggestion
      key={suggestion.id}
      suggestion={suggestion}
      acceptSuggestion={acceptSuggestion}
      rejectSuggestion={rejectSuggestion}
      updateSuggestion={updateSuggestion}
      formik={formik}
    />
  ));
};
